/**
 * Noise Suppression (feature guide)
 *
 * Screenshots were taken from the Sokuji v0.41.1 desktop app.
 */

import { useI18n, Locale } from '@/lib/i18n';
import { TutorialTemplate, TutorialData } from './TutorialTemplate';

const en: TutorialData = {
  "pageTitle": "Noise Suppression",
  "backLink": "Back to Documentation",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Overview",
    "content": "Noise suppression cleans up your microphone before your speech is translated, so background noise, typing and fans are not mistaken for speech."
  },
  "steps": [
    {
      "title": "Choose a level",
      "content": "In Settings, under <strong>Microphone</strong>, choose <strong>Noise Suppression</strong>: <strong>Off</strong>, <strong>Standard</strong> (lightweight, lowest delay) or <strong>Enhanced</strong> (strongest, the default). If Enhanced cannot run on your computer, Sokuji uses Standard instead. You can change it during a session.",
      "screenshot": "01-noise-suppression.png"
    },
    {
      "title": "How the two modes work",
      "content": "Both modes run entirely on your computer; your microphone audio is not sent anywhere for noise suppression.<br><br><strong>Standard</strong> uses RNNoise, a very small neural network combined with classic signal processing. Every 10 ms it splits the sound into frequency bands and turns down the bands it judges to be noise. It is light and adds almost no delay, and works best on steady noise such as fans or air conditioning.<br><br><strong>Enhanced</strong> uses GTCRN, a larger neural network. It looks at the whole frequency spectrum in overlapping 32 ms windows and predicts which parts belong to your voice, then rebuilds the audio from those parts. It copes better with changing noise such as typing, other people talking or street sounds, at the cost of more processing and a few tens of milliseconds of extra delay."
    }
  ],
  "tips": {
    "title": "Tips",
    "items": [
      "Try Off if your headset already cancels noise, or if your voice sounds clipped.",
      "Noise suppression applies to your microphone only, not to Other’s audio.",
      "Echo cancellation is always on as well. On Windows and macOS it removes everything your computer plays from your microphone, the meeting and Sokuji’s own translated voice included. On Linux, and in older browsers, it covers less, so wear headphones there."
    ]
  },
  "faq": {
    "title": "Frequently Asked Questions",
    "items": [
      {
        "question": "Which level should I use?",
        "answer": "Keep Enhanced unless it causes problems. Standard uses less processing; Off leaves your microphone as it is."
      }
    ]
  },
  "troubleshooting": {
    "title": "Troubleshooting",
    "content": "Still stuck? Send us feedback from the account menu, or write to <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. You can also ask in <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const zh: TutorialData = {
  "pageTitle": "噪声抑制",
  "backLink": "返回文档",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "概述",
    "content": "噪声抑制会在翻译你的语音之前清理麦克风输入，避免背景噪音、打字声和风扇声被误认为语音。"
  },
  "steps": [
    {
      "title": "选择级别",
      "content": "在设置的<strong>麦克风</strong>下，选择<strong>噪声抑制</strong>：<strong>关闭</strong>、<strong>标准</strong>（轻量，延迟最低）或<strong>增强</strong>（效果最强，默认）。如果你的电脑无法运行增强，Sokuji 会改用标准。会话期间也可以更改。",
      "screenshot": "01-noise-suppression.png"
    },
    {
      "title": "两种模式的工作原理",
      "content": "两种模式都完全在你的电脑上运行；为了噪声抑制，你的麦克风音频不会被发送到任何地方。<br><br><strong>标准</strong>使用 RNNoise，这是一个非常小的神经网络，结合了经典的信号处理。它每 10 ms 将声音拆分为多个频段，并压低它判断为噪音的频段。它很轻量，几乎不增加延迟，最适合风扇、空调这类稳定的噪音。<br><br><strong>增强</strong>使用 GTCRN，一个更大的神经网络。它以相互重叠的 32 ms 窗口查看整个频谱，预测哪些部分属于你的声音，再用这些部分重建音频。它更能应对变化的噪音，例如打字声、其他人说话或街道噪音，代价是更多的处理量，以及几十毫秒的额外延迟。"
    }
  ],
  "tips": {
    "title": "提示",
    "items": [
      "如果你的耳机本身已经有降噪功能，或者你的声音听起来断断续续，请试试关闭。",
      "噪声抑制只作用于你的麦克风，不作用于对方音频。",
      "回声消除也始终开启。在 Windows 和 macOS 上，它会从你的麦克风中去除电脑播放的所有声音，包括会议的声音和 Sokuji 自己的翻译语音。在 Linux 上以及较旧的浏览器中，它能去除的较少，因此请戴耳机。"
    ]
  },
  "faq": {
    "title": "常见问题",
    "items": [
      {
        "question": "应该用哪个级别？",
        "answer": "除非出现问题，否则保持增强即可。标准占用的处理资源更少；关闭则保持麦克风原样。"
      }
    ]
  },
  "troubleshooting": {
    "title": "故障排除",
    "content": "仍然无法解决？可以从账号菜单向我们发送反馈，或发送邮件至 <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>。你也可以在 <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a> 中提问。"
  }
};

const ja: TutorialData = {
  "pageTitle": "ノイズ抑制",
  "backLink": "ドキュメントに戻る",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "概要",
    "content": "ノイズ抑制は、発言を翻訳する前にマイクの音声をクリーンにし、周囲の雑音やタイピング音、ファンの音が発話と誤認されないようにします。"
  },
  "steps": [
    {
      "title": "レベルを選ぶ",
      "content": "設定の<strong>マイクロフォン</strong>で<strong>ノイズ抑制</strong>を選びます。<strong>オフ</strong>、<strong>スタンダード</strong>（軽量で遅延が最小）、<strong>エンハンスド</strong>（最も強力で既定）のいずれかです。お使いのパソコンでエンハンスドを実行できない場合、Sokuji は代わりにスタンダードを使います。セッション中でも変更できます。",
      "screenshot": "01-noise-suppression.png"
    },
    {
      "title": "2 つのモードのしくみ",
      "content": "どちらのモードもすべてお使いのパソコン上で動作し、ノイズ抑制のためにマイクの音声がどこかへ送信されることはありません。<br><br><strong>スタンダード</strong>は、ごく小さなニューラルネットワークと従来の信号処理を組み合わせた RNNoise を使います。10 ms ごとに音を周波数帯域に分け、ノイズと判断した帯域の音量を下げます。軽量で遅延はほとんど増えず、ファンやエアコンのような一定のノイズに最も効果的です。<br><br><strong>エンハンスド</strong>は、より大きなニューラルネットワークである GTCRN を使います。重なり合う 32 ms の区間ごとに周波数スペクトル全体を見て、どの部分があなたの声かを予測し、その部分から音声を組み立て直します。タイピング音や他の人の話し声、街の音など、変化するノイズにより強い一方で、処理が重くなり、数十ミリ秒の遅延が加わります。"
    }
  ],
  "tips": {
    "title": "ヒント",
    "items": [
      "ヘッドセットにノイズキャンセル機能がすでにある場合や、声が途切れて聞こえる場合は、オフを試してください。",
      "ノイズ抑制はあなたのマイクにのみ適用され、相手の音声には適用されません。",
      "エコーキャンセルも常にオンです。Windows と macOS では、会議の音や Sokuji 自身の翻訳音声も含め、パソコンで再生されるすべての音をマイクから取り除きます。Linux や古いブラウザーでは取り除ける範囲が狭いため、ヘッドホンを着けてください。"
    ]
  },
  "faq": {
    "title": "よくある質問",
    "items": [
      {
        "question": "どのレベルを使えばよいですか？",
        "answer": "問題がない限りエンハンスドのままにしてください。スタンダードは処理が軽く、オフではマイクの音声がそのまま使われます。"
      }
    ]
  },
  "troubleshooting": {
    "title": "トラブルシューティング",
    "content": "それでも解決しない場合は、アカウントメニューからフィードバックを送るか、<a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a> までご連絡ください。<a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a> で質問することもできます。"
  }
};

const ko: TutorialData = {
  "pageTitle": "노이즈 억제",
  "backLink": "문서로 돌아가기",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "개요",
    "content": "노이즈 억제는 내 말이 번역되기 전에 마이크 소리를 정리하여, 배경 소음이나 타자 소리, 팬 소리가 음성으로 오인되지 않게 합니다."
  },
  "steps": [
    {
      "title": "수준 선택",
      "content": "설정의 <strong>마이크</strong>에서 <strong>노이즈 억제</strong>를 선택하세요. <strong>끄기</strong>, <strong>표준</strong>(가볍고 지연이 가장 적음), <strong>향상</strong>(가장 강력하며 기본값) 중에서 고릅니다. 컴퓨터에서 향상을 실행할 수 없으면 Sokuji가 대신 표준을 사용합니다. 세션 중에도 바꿀 수 있습니다.",
      "screenshot": "01-noise-suppression.png"
    },
    {
      "title": "두 모드의 작동 방식",
      "content": "두 모드 모두 컴퓨터에서만 실행되며, 노이즈 억제를 위해 마이크 오디오가 외부로 전송되지 않습니다.<br><br><strong>표준</strong>은 아주 작은 신경망과 전통적인 신호 처리를 결합한 RNNoise를 사용합니다. 10 ms마다 소리를 주파수 대역으로 나누고, 노이즈로 판단한 대역의 소리를 줄입니다. 가볍고 지연이 거의 늘지 않으며, 팬이나 에어컨처럼 일정한 소음에 가장 효과적입니다.<br><br><strong>향상</strong>은 더 큰 신경망인 GTCRN을 사용합니다. 서로 겹치는 32 ms 구간 단위로 전체 주파수 스펙트럼을 살펴 어느 부분이 내 목소리인지 예측한 뒤, 그 부분으로 오디오를 다시 만듭니다. 타자 소리, 다른 사람의 말소리, 거리 소음처럼 변하는 소음에 더 잘 대응하지만, 처리량이 늘고 수십 밀리초의 지연이 추가됩니다."
    }
  ],
  "tips": {
    "title": "팁",
    "items": [
      "헤드셋에 이미 노이즈 캔슬링 기능이 있거나 목소리가 끊겨 들린다면 끄기를 시도해 보세요.",
      "노이즈 억제는 내 마이크에만 적용되며 상대방 오디오에는 적용되지 않습니다.",
      "에코 제거도 항상 켜져 있습니다. Windows와 macOS에서는 회의 소리와 Sokuji 자체의 번역 음성을 포함해 컴퓨터에서 재생되는 모든 소리를 마이크에서 제거합니다. Linux와 오래된 브라우저에서는 제거하는 범위가 좁으므로 헤드폰을 착용하세요."
    ]
  },
  "faq": {
    "title": "자주 묻는 질문",
    "items": [
      {
        "question": "어떤 수준을 사용해야 하나요?",
        "answer": "문제가 없다면 향상을 유지하세요. 표준은 처리량이 적고, 끄기는 마이크 소리를 그대로 둡니다."
      }
    ]
  },
  "troubleshooting": {
    "title": "문제 해결",
    "content": "그래도 해결되지 않나요? 계정 메뉴에서 피드백을 보내거나 <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>로 이메일을 보내 주세요. <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>에서 질문할 수도 있습니다."
  }
};

const es: TutorialData = {
  "pageTitle": "Supresión de ruido",
  "backLink": "Volver a la documentación",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Descripción general",
    "content": "La supresión de ruido limpia el sonido de tu micrófono antes de traducir lo que dices, para que el ruido de fondo, el tecleo o los ventiladores no se confundan con habla."
  },
  "steps": [
    {
      "title": "Elige un nivel",
      "content": "En Configuración, en <strong>Micrófono</strong>, elige <strong>Supresión de ruido</strong>: <strong>Desactivado</strong>, <strong>Estándar</strong> (ligera, con el menor retardo) o <strong>Mejorado</strong> (la más potente, la predeterminada). Si Mejorado no puede funcionar en tu ordenador, Sokuji usa Estándar en su lugar. Puedes cambiarlo durante una sesión.",
      "screenshot": "01-noise-suppression.png"
    },
    {
      "title": "Cómo funcionan los dos modos",
      "content": "Ambos modos se ejecutan por completo en tu ordenador; el audio de tu micrófono no se envía a ningún sitio para la supresión de ruido.<br><br><strong>Estándar</strong> usa RNNoise, una red neuronal muy pequeña combinada con procesamiento de señal clásico. Cada 10 ms divide el sonido en bandas de frecuencia y baja el volumen de las bandas que considera ruido. Es ligero, casi no añade retardo y funciona mejor con ruido constante, como ventiladores o aire acondicionado.<br><br><strong>Mejorado</strong> usa GTCRN, una red neuronal más grande. Analiza todo el espectro de frecuencias en ventanas superpuestas de 32 ms, predice qué partes corresponden a tu voz y reconstruye el audio a partir de ellas. Se defiende mejor con ruido cambiante, como el tecleo, otras personas hablando o el ruido de la calle, a cambio de más procesamiento y unas decenas de milisegundos de retardo adicional."
    }
  ],
  "tips": {
    "title": "Consejos",
    "items": [
      "Prueba Desactivado si tus auriculares ya cancelan el ruido o si tu voz suena entrecortada.",
      "La supresión de ruido solo se aplica a tu micrófono, no al Audio del otro.",
      "La cancelación de eco también está siempre activada. En Windows y macOS elimina de tu micrófono todo lo que reproduce tu ordenador, incluidas la reunión y la propia voz traducida de Sokuji. En Linux, y en navegadores antiguos, cubre menos, así que allí usa auriculares."
    ]
  },
  "faq": {
    "title": "Preguntas frecuentes",
    "items": [
      {
        "question": "¿Qué nivel debo usar?",
        "answer": "Deja Mejorado salvo que cause problemas. Estándar usa menos procesamiento; Desactivado deja tu micrófono tal cual."
      }
    ]
  },
  "troubleshooting": {
    "title": "Solución de problemas",
    "content": "¿Sigues atascado? Envíanos tus comentarios desde el menú de la cuenta o escribe a <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. También puedes preguntar en <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const fr: TutorialData = {
  "pageTitle": "Suppression du bruit",
  "backLink": "Retour à la documentation",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Aperçu",
    "content": "La suppression du bruit nettoie le son de votre microphone avant que vos paroles soient traduites, pour que le bruit de fond, la frappe au clavier ou les ventilateurs ne soient pas pris pour de la parole."
  },
  "steps": [
    {
      "title": "Choisissez un niveau",
      "content": "Dans les Paramètres, sous <strong>Microphone</strong>, choisissez <strong>Suppression du bruit</strong> : <strong>Désactivé</strong>, <strong>Standard</strong> (léger, délai le plus faible) ou <strong>Amélioré</strong> (le plus puissant, par défaut). Si Amélioré ne peut pas fonctionner sur votre ordinateur, Sokuji utilise Standard à la place. Vous pouvez le changer pendant une session.",
      "screenshot": "01-noise-suppression.png"
    },
    {
      "title": "Fonctionnement des deux modes",
      "content": "Les deux modes s’exécutent entièrement sur votre ordinateur ; le son de votre microphone n’est envoyé nulle part pour la suppression du bruit.<br><br><strong>Standard</strong> utilise RNNoise, un tout petit réseau de neurones associé à un traitement du signal classique. Toutes les 10 ms, il découpe le son en bandes de fréquences et atténue celles qu’il juge être du bruit. Léger, il n’ajoute presque aucun délai et donne ses meilleurs résultats sur les bruits constants comme les ventilateurs ou la climatisation.<br><br><strong>Amélioré</strong> utilise GTCRN, un réseau de neurones plus grand. Il examine tout le spectre de fréquences par fenêtres de 32 ms qui se chevauchent, prédit quelles parties appartiennent à votre voix, puis reconstruit le son à partir de ces parties. Il gère mieux les bruits changeants comme la frappe au clavier, d’autres personnes qui parlent ou les bruits de la rue, au prix de davantage de traitement et de quelques dizaines de millisecondes de délai supplémentaire."
    }
  ],
  "tips": {
    "title": "Conseils",
    "items": [
      "Essayez Désactivé si votre casque supprime déjà le bruit, ou si votre voix semble hachée.",
      "La suppression du bruit ne s’applique qu’à votre microphone, pas à l’Audio de l'autre.",
      "L’annulation d’écho est elle aussi toujours active. Sous Windows et macOS, elle retire de votre microphone tout ce que joue votre ordinateur, y compris la réunion et la voix traduite de Sokuji. Sous Linux, et dans les navigateurs plus anciens, elle en couvre moins : portez donc un casque."
    ]
  },
  "faq": {
    "title": "Questions fréquentes",
    "items": [
      {
        "question": "Quel niveau dois-je utiliser ?",
        "answer": "Gardez Amélioré sauf s’il pose problème. Standard demande moins de traitement ; Désactivé laisse votre microphone tel quel."
      }
    ]
  },
  "troubleshooting": {
    "title": "Dépannage",
    "content": "Toujours bloqué ? Envoyez-nous un retour depuis le menu du compte, ou écrivez à <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. Vous pouvez aussi poser votre question sur <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const ar: TutorialData = {
  "pageTitle": "قمع الضوضاء",
  "backLink": "العودة إلى التوثيق",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "نظرة عامة",
    "content": "ينظّف قمع الضوضاء صوت ميكروفونك قبل ترجمة كلامك، حتى لا تُحسب ضوضاء الخلفية والكتابة على لوحة المفاتيح والمراوح كلامًا."
  },
  "steps": [
    {
      "title": "اختر مستوى",
      "content": "في الإعدادات، تحت <strong>ميكروفون</strong>، اختر <strong>قمع الضوضاء</strong>: <strong>إيقاف</strong>، أو <strong>قياسي</strong> (خفيف، أقل تأخير)، أو <strong>محسّن</strong> (الأقوى، والافتراضي). إذا تعذّر تشغيل «محسّن» على جهازك، يستخدم Sokuji «قياسي» بدلًا منه. يمكنك تغييره أثناء الجلسة.",
      "screenshot": "01-noise-suppression.png"
    },
    {
      "title": "كيف يعمل الوضعان",
      "content": "يعمل الوضعان بالكامل على جهازك، ولا يُرسَل صوت ميكروفونك إلى أي مكان لأجل قمع الضوضاء.<br><br>يستخدم <strong>قياسي</strong> RNNoise، وهي شبكة عصبية صغيرة جدًا مقترنة بمعالجة الإشارات التقليدية. كل 10 ms تقسّم الصوت إلى نطاقات ترددية وتخفض النطاقات التي تعدّها ضوضاء. إنه خفيف ولا يضيف تأخيرًا يُذكر، ويعمل على أفضل وجه مع الضوضاء الثابتة مثل المراوح أو مكيّف الهواء.<br><br>يستخدم <strong>محسّن</strong> GTCRN، وهي شبكة عصبية أكبر. تفحص الطيف الترددي كاملًا عبر نوافذ متداخلة مدتها 32 ms، وتتنبأ بالأجزاء التي تنتمي إلى صوتك، ثم تعيد بناء الصوت من هذه الأجزاء. يتعامل بشكل أفضل مع الضوضاء المتغيرة مثل الكتابة على لوحة المفاتيح أو حديث أشخاص آخرين أو أصوات الشارع، مقابل معالجة أكبر وتأخير إضافي يبلغ بضع عشرات من الملّي ثانية."
    }
  ],
  "tips": {
    "title": "نصائح",
    "items": [
      "جرّب «إيقاف» إذا كانت سماعة الرأس تلغي الضوضاء بالفعل، أو إذا بدا صوتك متقطعًا.",
      "يُطبَّق قمع الضوضاء على ميكروفونك فقط، لا على صوت الآخر.",
      "إلغاء الصدى مفعّل دائمًا أيضًا. على Windows و macOS يزيل من ميكروفونك كل ما يشغّله جهازك، بما في ذلك الاجتماع وصوت Sokuji المترجَم نفسه. أما على Linux وفي المتصفحات الأقدم فتغطيته أقل، لذا ارتدِ سماعات الرأس هناك."
    ]
  },
  "faq": {
    "title": "الأسئلة الشائعة",
    "items": [
      {
        "question": "أي مستوى أستخدم؟",
        "answer": "أبقِ «محسّن» ما لم يسبب مشكلات. يستهلك «قياسي» معالجة أقل؛ ويترك «إيقاف» ميكروفونك كما هو."
      }
    ]
  },
  "troubleshooting": {
    "title": "استكشاف الأخطاء وإصلاحها",
    "content": "هل ما زلت تواجه مشكلة؟ أرسل إلينا ملاحظاتك من قائمة الحساب، أو راسلنا على <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. يمكنك أيضًا طرح سؤالك في <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const pt: TutorialData = {
  "pageTitle": "Supressão de Ruído",
  "backLink": "Voltar à documentação",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Visão geral",
    "content": "A supressão de ruído limpa o som do seu microfone antes que a sua fala seja traduzida, para que ruído de fundo, digitação e ventiladores não sejam confundidos com fala."
  },
  "steps": [
    {
      "title": "Escolha um nível",
      "content": "Em Configurações, em <strong>Microfone</strong>, escolha <strong>Supressão de Ruído</strong>: <strong>Desativado</strong>, <strong>Padrão</strong> (leve, com o menor atraso) ou <strong>Aprimorado</strong> (o mais forte, o padrão). Se o Aprimorado não puder ser executado no seu computador, o Sokuji usa o Padrão no lugar. Você pode alterar isso durante uma sessão.",
      "screenshot": "01-noise-suppression.png"
    },
    {
      "title": "Como os dois modos funcionam",
      "content": "Os dois modos rodam inteiramente no seu computador; o áudio do seu microfone não é enviado para lugar nenhum para a supressão de ruído.<br><br>O <strong>Padrão</strong> usa o RNNoise, uma rede neural bem pequena combinada com processamento de sinal clássico. A cada 10 ms, ele divide o som em faixas de frequência e abaixa as faixas que considera ruído. É leve, quase não adiciona atraso e funciona melhor com ruído constante, como ventiladores ou ar-condicionado.<br><br>O <strong>Aprimorado</strong> usa o GTCRN, uma rede neural maior. Ele analisa todo o espectro de frequências em janelas sobrepostas de 32 ms, prevê quais partes pertencem à sua voz e reconstrói o áudio a partir delas. Lida melhor com ruído variável, como digitação, outras pessoas falando ou barulho da rua, ao custo de mais processamento e algumas dezenas de milissegundos de atraso extra."
    }
  ],
  "tips": {
    "title": "Dicas",
    "items": [
      "Experimente Desativado se o seu headset já cancela ruído ou se a sua voz soa cortada.",
      "A supressão de ruído se aplica só ao seu microfone, não ao Áudio do outro.",
      "O cancelamento de eco também fica sempre ativado. No Windows e no macOS, ele remove do seu microfone tudo o que o computador reproduz, incluindo a reunião e a própria voz traduzida do Sokuji. No Linux, e em navegadores mais antigos, ele cobre menos, então use fones de ouvido nesses casos."
    ]
  },
  "faq": {
    "title": "Perguntas frequentes",
    "items": [
      {
        "question": "Qual nível devo usar?",
        "answer": "Mantenha o Aprimorado, a menos que ele cause problemas. O Padrão usa menos processamento; Desativado deixa o microfone como está."
      }
    ]
  },
  "troubleshooting": {
    "title": "Solução de problemas",
    "content": "Ainda com dificuldades? Envie seu feedback pelo menu da conta ou escreva para <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. Você também pode perguntar no <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const ru: TutorialData = {
  "pageTitle": "Шумоподавление",
  "backLink": "Назад к документации",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Обзор",
    "content": "Шумоподавление очищает сигнал микрофона до перевода вашей речи, чтобы фоновый шум, стук клавиш и вентиляторы не принимались за речь."
  },
  "steps": [
    {
      "title": "Выберите уровень",
      "content": "В настройках, в разделе <strong>Микрофон</strong>, выберите <strong>Шумоподавление</strong>: <strong>Выкл</strong>, <strong>Стандартный</strong> (лёгкий, минимальная задержка) или <strong>Улучшенный</strong> (самый сильный, по умолчанию). Если «Улучшенный» не может работать на вашем компьютере, Sokuji использует «Стандартный». Уровень можно менять во время сеанса.",
      "screenshot": "01-noise-suppression.png"
    },
    {
      "title": "Как работают два режима",
      "content": "Оба режима работают полностью на вашем компьютере: звук микрофона никуда не отправляется для шумоподавления.<br><br><strong>Стандартный</strong> использует RNNoise — очень маленькую нейросеть в сочетании с классической обработкой сигнала. Каждые 10 ms он разбивает звук на частотные полосы и приглушает те, которые считает шумом. Он лёгкий, почти не добавляет задержки и лучше всего справляется с постоянным шумом, например от вентиляторов или кондиционера.<br><br><strong>Улучшенный</strong> использует GTCRN — более крупную нейросеть. Он анализирует весь частотный спектр в перекрывающихся окнах по 32 ms, определяет, какие части относятся к вашему голосу, и восстанавливает звук из этих частей. Он лучше справляется с меняющимся шумом — стуком клавиш, речью других людей или уличными звуками, — но требует больше вычислений и добавляет несколько десятков миллисекунд задержки."
    }
  ],
  "tips": {
    "title": "Советы",
    "items": [
      "Попробуйте «Выкл», если гарнитура уже подавляет шум или ваш голос звучит обрывисто.",
      "Шумоподавление применяется только к вашему микрофону, а не к аудио собеседника.",
      "Эхоподавление тоже всегда включено. В Windows и macOS оно удаляет из вашего микрофона всё, что воспроизводит компьютер, включая встречу и собственный переведённый голос Sokuji. В Linux и в старых браузерах оно охватывает меньше, поэтому там надевайте наушники."
    ]
  },
  "faq": {
    "title": "Часто задаваемые вопросы",
    "items": [
      {
        "question": "Какой уровень выбрать?",
        "answer": "Оставьте «Улучшенный», если он не вызывает проблем. «Стандартный» требует меньше вычислений; «Выкл» оставляет микрофон как есть."
      }
    ]
  },
  "troubleshooting": {
    "title": "Устранение неполадок",
    "content": "Всё ещё не получается? Отправьте нам отзыв через меню учётной записи или напишите на <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. Также можно задать вопрос в <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const de: TutorialData = {
  "pageTitle": "Rauschunterdrückung",
  "backLink": "Zurück zur Dokumentation",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Übersicht",
    "content": "Die Rauschunterdrückung bereinigt Ihr Mikrofonsignal, bevor Ihre Sprache übersetzt wird, damit Hintergrundgeräusche, Tippen und Lüfter nicht für Sprache gehalten werden."
  },
  "steps": [
    {
      "title": "Eine Stufe wählen",
      "content": "Wählen Sie in den Einstellungen unter <strong>Mikrofon</strong> bei <strong>Rauschunterdrückung</strong>: <strong>Aus</strong>, <strong>Standard</strong> (leichtgewichtig, geringste Verzögerung) oder <strong>Erweitert</strong> (am stärksten, die Voreinstellung). Kann „Erweitert“ auf Ihrem Computer nicht laufen, verwendet Sokuji stattdessen „Standard“. Sie können die Stufe während einer Sitzung ändern.",
      "screenshot": "01-noise-suppression.png"
    },
    {
      "title": "So funktionieren die beiden Modi",
      "content": "Beide Modi laufen vollständig auf Ihrem Computer; Ihr Mikrofonsignal wird für die Rauschunterdrückung nirgendwohin gesendet.<br><br><strong>Standard</strong> verwendet RNNoise, ein sehr kleines neuronales Netz in Kombination mit klassischer Signalverarbeitung. Alle 10 ms zerlegt es den Klang in Frequenzbänder und senkt die Bänder ab, die es als Rauschen einstuft. Es ist leichtgewichtig, fügt kaum Verzögerung hinzu und wirkt am besten bei gleichmäßigen Geräuschen wie Lüftern oder Klimaanlagen.<br><br><strong>Erweitert</strong> verwendet GTCRN, ein größeres neuronales Netz. Es betrachtet das gesamte Frequenzspektrum in überlappenden Fenstern von 32 ms, sagt voraus, welche Anteile zu Ihrer Stimme gehören, und setzt das Audio aus diesen Anteilen neu zusammen. Mit wechselnden Geräuschen wie Tippen, anderen Sprechern oder Straßenlärm kommt es besser zurecht, braucht dafür aber mehr Rechenleistung und fügt einige zehn Millisekunden zusätzliche Verzögerung hinzu."
    }
  ],
  "tips": {
    "title": "Tipps",
    "items": [
      "Versuchen Sie „Aus“, wenn Ihr Headset bereits Geräusche unterdrückt oder Ihre Stimme abgehackt klingt.",
      "Die Rauschunterdrückung gilt nur für Ihr Mikrofon, nicht für das Audio des Gegenübers.",
      "Auch die Echounterdrückung ist immer eingeschaltet. Unter Windows und macOS entfernt sie alles, was Ihr Computer wiedergibt, aus Ihrem Mikrofonsignal, auch das Meeting und Sokujis eigene übersetzte Stimme. Unter Linux und in älteren Browsern deckt sie weniger ab, tragen Sie dort also Kopfhörer."
    ]
  },
  "faq": {
    "title": "Häufig gestellte Fragen",
    "items": [
      {
        "question": "Welche Stufe sollte ich verwenden?",
        "answer": "Bleiben Sie bei „Erweitert“, sofern es keine Probleme verursacht. „Standard“ braucht weniger Rechenleistung; „Aus“ lässt Ihr Mikrofon unverändert."
      }
    ]
  },
  "troubleshooting": {
    "title": "Fehlerbehebung",
    "content": "Kommen Sie nicht weiter? Senden Sie uns Feedback über das Kontomenü oder schreiben Sie an <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. Sie können auch in den <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a> fragen."
  }
};

const uk: TutorialData = {
  "pageTitle": "Придушення шуму",
  "backLink": "Назад до документації",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Огляд",
    "content": "Придушення шуму очищає сигнал мікрофона до перекладу вашого мовлення, щоб фоновий шум, стукіт клавіш і вентилятори не сприймалися як мовлення."
  },
  "steps": [
    {
      "title": "Виберіть рівень",
      "content": "У налаштуваннях, у розділі <strong>Мікрофон</strong>, виберіть <strong>Придушення шуму</strong>: <strong>Вимк</strong>, <strong>Стандартний</strong> (легкий, найменша затримка) або <strong>Покращений</strong> (найсильніший, за замовчуванням). Якщо «Покращений» не може працювати на вашому комп’ютері, Sokuji використовує «Стандартний». Рівень можна змінювати під час сеансу.",
      "screenshot": "01-noise-suppression.png"
    },
    {
      "title": "Як працюють два режими",
      "content": "Обидва режими працюють повністю на вашому комп’ютері: звук мікрофона нікуди не надсилається для придушення шуму.<br><br><strong>Стандартний</strong> використовує RNNoise — дуже маленьку нейромережу в поєднанні з класичною обробкою сигналу. Кожні 10 ms він розбиває звук на частотні смуги й приглушує ті, які вважає шумом. Він легкий, майже не додає затримки й найкраще впорається з рівномірним шумом, як-от від вентиляторів чи кондиціонера.<br><br><strong>Покращений</strong> використовує GTCRN — більшу нейромережу. Він аналізує весь частотний спектр у вікнах по 32 ms, що перекриваються, визначає, які частини належать вашому голосу, і відновлює звук із цих частин. Він краще впорається зі змінним шумом — стукотом клавіш, розмовами інших людей або вуличними звуками, — але потребує більше обчислень і додає кілька десятків мілісекунд затримки."
    }
  ],
  "tips": {
    "title": "Поради",
    "items": [
      "Спробуйте «Вимк», якщо гарнітура вже придушує шум або ваш голос звучить уривчасто.",
      "Придушення шуму застосовується лише до вашого мікрофона, а не до аудіо співрозмовника.",
      "Придушення відлуння теж завжди ввімкнене. У Windows і macOS воно прибирає з вашого мікрофона все, що відтворює комп’ютер, зокрема зустріч і власний перекладений голос Sokuji. У Linux і в старих браузерах воно охоплює менше, тож там надягайте навушники."
    ]
  },
  "faq": {
    "title": "Часті запитання",
    "items": [
      {
        "question": "Який рівень вибрати?",
        "answer": "Залиште «Покращений», якщо він не спричиняє проблем. «Стандартний» потребує менше обчислень; «Вимк» залишає мікрофон як є."
      }
    ]
  },
  "troubleshooting": {
    "title": "Усунення несправностей",
    "content": "Досі не виходить? Надішліть нам відгук через меню облікового запису або напишіть на <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. Також можна запитати в <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const it: TutorialData = {
  "pageTitle": "Soppressione del rumore",
  "backLink": "Torna alla documentazione",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Panoramica",
    "content": "La soppressione del rumore ripulisce l’audio del microfono prima che il tuo parlato venga tradotto, così rumore di fondo, digitazione e ventole non vengono scambiati per parlato."
  },
  "steps": [
    {
      "title": "Scegli un livello",
      "content": "In Impostazioni, sotto <strong>Microfono</strong>, scegli <strong>Soppressione del rumore</strong>: <strong>Disattivato</strong>, <strong>Standard</strong> (leggero, con il ritardo minimo) o <strong>Avanzato</strong> (il più efficace, predefinito). Se Avanzato non può funzionare sul tuo computer, Sokuji usa Standard. Puoi cambiarlo durante una sessione.",
      "screenshot": "01-noise-suppression.png"
    },
    {
      "title": "Come funzionano le due modalità",
      "content": "Entrambe le modalità funzionano interamente sul tuo computer: l’audio del microfono non viene inviato da nessuna parte per la soppressione del rumore.<br><br><strong>Standard</strong> usa RNNoise, una rete neurale molto piccola abbinata all’elaborazione del segnale classica. Ogni 10 ms divide il suono in bande di frequenza e abbassa quelle che giudica rumore. È leggero, non aggiunge quasi alcun ritardo e rende al meglio con rumori costanti come ventole o aria condizionata.<br><br><strong>Avanzato</strong> usa GTCRN, una rete neurale più grande. Analizza l’intero spettro di frequenze in finestre sovrapposte di 32 ms, prevede quali parti appartengono alla tua voce e ricostruisce l’audio a partire da quelle. Gestisce meglio i rumori variabili, come la digitazione, altre persone che parlano o i rumori della strada, al prezzo di più elaborazione e qualche decina di millisecondi di ritardo in più."
    }
  ],
  "tips": {
    "title": "Consigli",
    "items": [
      "Prova Disattivato se le tue cuffie cancellano già il rumore o se la tua voce risulta troncata.",
      "La soppressione del rumore si applica solo al tuo microfono, non all’Audio dell'altro.",
      "Anche la cancellazione dell’eco è sempre attiva. Su Windows e macOS rimuove dal microfono tutto ciò che riproduce il computer, compresi la riunione e la voce tradotta di Sokuji. Su Linux, e nei browser meno recenti, copre meno: lì indossa le cuffie."
    ]
  },
  "faq": {
    "title": "Domande frequenti",
    "items": [
      {
        "question": "Quale livello devo usare?",
        "answer": "Tieni Avanzato, a meno che non crei problemi. Standard richiede meno elaborazione; Disattivato lascia il microfono così com’è."
      }
    ]
  },
  "troubleshooting": {
    "title": "Risoluzione dei problemi",
    "content": "Ancora bloccato? Inviaci un feedback dal menu dell’account, oppure scrivi a <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. Puoi anche chiedere su <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const translations: Record<Locale, TutorialData> = { en, zh, ja, ko, es, fr, ar, pt, ru, de, uk, it };

export function FeatureNoiseSuppression() {
  const { locale } = useI18n();
  const data = translations[locale] || translations.en;

  return <TutorialTemplate data={data} screenshotBasePath="/features/noise-suppression" />;
}
